import React, { useState, useEffect } from 'react';
import { reportesAuto } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { 
  Mail, Plus, Edit2, Trash2, Send, Clock, Calendar, 
  FileText, PieChart, Wallet, TrendingUp, CheckCircle, XCircle
} from 'lucide-react';

const TIPOS_REPORTE = [
  { value: 'dashboard', label: 'Dashboard KPIs', icon: PieChart, desc: 'Resumen de indicadores clave' },
  { value: 'estado_resultados', label: 'Estado de Resultados', icon: FileText, desc: 'P&L del período' },
  { value: 'flujo_caja', label: 'Flujo de Caja', icon: Wallet, desc: 'Movimientos de efectivo' },
  { value: 'rentabilidad', label: 'Rentabilidad', icon: TrendingUp, desc: 'Análisis por cliente' },
];

const FRECUENCIAS = [
  { value: 'diario', label: 'Diario', desc: 'Todos los días a las 8:00 AM' },
  { value: 'semanal', label: 'Semanal', desc: 'Cada lunes a las 8:00 AM' },
  { value: 'quincenal', label: 'Quincenal', desc: 'Días 1 y 15 de cada mes' },
  { value: 'mensual', label: 'Mensual', desc: 'Primer día del mes' },
];

const ReportesAuto = () => {
  const [reportes, setReportes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  
  // Form
  const [formData, setFormData] = useState({
    tipo_reporte: '',
    frecuencia: 'semanal',
    correo_destino: '',
    activo: true
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await reportesAuto.getAll();
      setReportes(response.data);
    } catch (error) {
      toast.error('Error al cargar reportes');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async () => {
    if (!formData.tipo_reporte || !formData.correo_destino) {
      toast.error('Completa los campos requeridos');
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.correo_destino)) {
      toast.error('Correo electrónico inválido');
      return;
    }

    try {
      if (editingItem) {
        await reportesAuto.update(editingItem.id, formData);
        toast.success('Reporte actualizado');
      } else {
        await reportesAuto.create(formData);
        toast.success('Reporte programado');
      }
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este reporte programado?')) return;
    try {
      await reportesAuto.delete(id);
      toast.success('Reporte eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleSendNow = async (id) => {
    setSendingId(id);
    try {
      const response = await reportesAuto.enviar(id);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Error al enviar reporte');
    } finally {
      setSendingId(null);
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await reportesAuto.update(item.id, { ...item, activo: !item.activo });
      toast.success(item.activo ? 'Reporte pausado' : 'Reporte activado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const resetForm = () => {
    setFormData({
      tipo_reporte: '',
      frecuencia: 'semanal',
      correo_destino: '',
      activo: true
    });
    setEditingItem(null);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({
      tipo_reporte: item.tipo_reporte,
      frecuencia: item.frecuencia,
      correo_destino: item.correo_destino,
      activo: item.activo
    });
    setShowDialog(true);
  };

  const getTipoInfo = (tipo) => TIPOS_REPORTE.find(t => t.value === tipo) || TIPOS_REPORTE[0];
  const getFrecuenciaInfo = (frec) => FRECUENCIAS.find(f => f.value === frec) || FRECUENCIAS[1];

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Nunca';
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  // Stats
  const stats = {
    total: reportes.length,
    activos: reportes.filter(r => r.activo).length,
    enviados: reportes.filter(r => r.ultimo_envio).length
  };

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="reportes-auto-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="w-6 h-6 text-primary" />
            Reportes Automáticos
          </h1>
          <p className="text-muted-foreground text-sm">Programa envíos de reportes por correo</p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="new-report-btn">
          <Plus className="w-4 h-4 mr-2" />
          Programar Reporte
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Reportes</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Activos</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.activos}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Enviados</p>
                <p className="text-2xl font-bold">{stats.enviados}</p>
              </div>
              <Send className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Automatización de Reportes</p>
              <p className="text-sm text-blue-700">
                Los reportes se generan y envían automáticamente según la frecuencia configurada. 
                Puedes enviar un reporte manualmente en cualquier momento usando el botón "Enviar Ahora".
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reports List */}
      <Card>
        <CardHeader>
          <CardTitle>Reportes Programados</CardTitle>
          <CardDescription>Gestiona tus reportes automáticos</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Cargando...</div>
          ) : reportes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Mail className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No hay reportes programados</p>
              <Button variant="outline" className="mt-4" onClick={() => { resetForm(); setShowDialog(true); }}>
                Programar primer reporte
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {reportes.map((item) => {
                const tipoInfo = getTipoInfo(item.tipo_reporte);
                const frecInfo = getFrecuenciaInfo(item.frecuencia);
                const TipoIcon = tipoInfo.icon;
                
                return (
                  <div 
                    key={item.id} 
                    className={`p-4 border rounded-lg transition-colors ${item.activo ? 'hover:bg-muted/50' : 'bg-muted/30 opacity-70'}`}
                    data-testid={`report-item-${item.id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-lg ${item.activo ? 'bg-primary/10' : 'bg-muted'}`}>
                          <TipoIcon className={`w-6 h-6 ${item.activo ? 'text-primary' : 'text-muted-foreground'}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold">{tipoInfo.label}</h3>
                            <Badge variant={item.activo ? 'default' : 'secondary'}>
                              {item.activo ? 'Activo' : 'Pausado'}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{tipoInfo.desc}</p>
                          
                          <div className="flex flex-wrap gap-4 mt-2 text-sm">
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <Mail className="w-4 h-4" />
                              {item.correo_destino}
                            </span>
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <Calendar className="w-4 h-4" />
                              {frecInfo.label}
                            </span>
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <Clock className="w-4 h-4" />
                              Último: {formatDate(item.ultimo_envio)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-2 mr-4">
                          <Switch
                            checked={item.activo}
                            onCheckedChange={() => handleToggleActive(item)}
                          />
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleSendNow(item.id)}
                          disabled={sendingId === item.id}
                        >
                          <Send className={`w-4 h-4 mr-1 ${sendingId === item.id ? 'animate-pulse' : ''}`} />
                          {sendingId === item.id ? 'Enviando...' : 'Enviar'}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(item)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Editar Reporte' : 'Programar Reporte'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tipo de Reporte *</Label>
              <Select value={formData.tipo_reporte} onValueChange={(v) => setFormData({ ...formData, tipo_reporte: v })}>
                <SelectTrigger data-testid="form-type">
                  <SelectValue placeholder="Selecciona tipo de reporte" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_REPORTE.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      <div className="flex items-center gap-2">
                        <t.icon className="w-4 h-4" />
                        <div>
                          <p>{t.label}</p>
                          <p className="text-xs text-muted-foreground">{t.desc}</p>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Frecuencia de Envío</Label>
              <Select value={formData.frecuencia} onValueChange={(v) => setFormData({ ...formData, frecuencia: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FRECUENCIAS.map(f => (
                    <SelectItem key={f.value} value={f.value}>
                      <div>
                        <p>{f.label}</p>
                        <p className="text-xs text-muted-foreground">{f.desc}</p>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Correo Destino *</Label>
              <Input
                type="email"
                value={formData.correo_destino}
                onChange={(e) => setFormData({ ...formData, correo_destino: e.target.value })}
                placeholder="reporte@empresa.com"
                data-testid="form-email"
              />
              <p className="text-xs text-muted-foreground mt-1">
                El reporte se enviará a este correo electrónico
              </p>
            </div>
            
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Estado del Reporte</p>
                <p className="text-sm text-muted-foreground">
                  {formData.activo ? 'El reporte se enviará según la frecuencia' : 'El reporte está pausado'}
                </p>
              </div>
              <Switch
                checked={formData.activo}
                onCheckedChange={(checked) => setFormData({ ...formData, activo: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} data-testid="submit-report">
              {editingItem ? 'Guardar' : 'Programar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportesAuto;
